import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { setAdminToken, getAdminToken } from "../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin login: email + password + 6-digit TOTP code. All three required ,
 * we deliberately don't split into two screens because that adds friction
 * for an already-savvy admin audience.
 *
 * After successful submit, the JWT is stashed in localStorage and the user
 * is sent to /admin/dashboard.
 */
export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If we already have a token, skip past the login.
  useEffect(() => {
    if (getAdminToken()) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/auth/login`, {
        email: email.trim(),
        password,
        totp_code: totp.replace(/\s+/g, ""),
      });
      setAdminToken(data.token);
      toast.success(`Welcome back, ${data.admin.email}`);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--charcoal)] grain-dark relative flex items-center justify-center p-6 overflow-hidden">
      <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--teal)]/15 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--lime)] mb-4">
            <span className="text-[var(--charcoal)] font-bold text-2xl font-['Outfit']">
              M
            </span>
          </div>
          <h1 className="brand-wordmark text-white text-5xl mb-2">MediTalk</h1>
          <p className="text-xs uppercase tracking-[0.18em] text-[var(--lime)] font-semibold mb-3">
            Admin
          </p>
          <p className="text-[var(--gray-light)] text-sm">
            Sign in with your email, password and authenticator code.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-[var(--light-border)] shadow-2xl p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <FieldRow Icon={Mail} label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@medicysltd.co.uk"
                required
                autoComplete="username"
                data-testid="admin-email"
              />
            </FieldRow>

            <FieldRow Icon={Lock} label="Password">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                data-testid="admin-password"
              />
            </FieldRow>

            <FieldRow Icon={KeyRound} label="Authenticator code">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/[^0-9 ]/g, ""))}
                className={`${inputCls} font-mono tracking-[0.35em]`}
                placeholder="123 456"
                required
                autoComplete="one-time-code"
                data-testid="admin-totp"
              />
            </FieldRow>

            <button
              type="submit"
              disabled={loading || !email || !password || totp.replace(/\s+/g, "").length < 6}
              className="btn-teal w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="text-[11.5px] text-[var(--text-muted)] text-center mt-5 leading-relaxed">
            Your authenticator code refreshes every 30 seconds. Locked out?
            Ask another admin to send you a fresh invite.
          </p>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[var(--gray-light)] hover:text-[var(--lime)] text-sm transition-colors"
          >
            <ArrowLeft size={14} />
            Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-12 pl-11 pr-4 rounded-xl border border-[var(--light-border)] bg-white text-[var(--charcoal)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20 transition-all";

const FieldRow = ({ Icon, label, children }) => (
  <div>
    <label className="block text-sm font-medium text-[var(--charcoal)] mb-2">
      {label}
    </label>
    <div className="relative">
      <Icon
        size={18}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
      />
      {children}
    </div>
  </div>
);
